import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../../../Context/useUser';
import API from '../../../Utils/api';
import Utils from '../../../Utils';
import Table from '../../../Components/Table/Table';
import styles from './ProgramCreator.module.css';
import Modal from '../../../Components/Modal/Modal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    MdAdd,
    MdDelete,
    MdEdit,
    MdArrowUpward,
    MdArrowDownward,
    MdChevronLeft,
    MdChevronRight,
    MdMoreVert
} from 'react-icons/md';
import { useForm } from 'react-hook-form';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { focusProgram, refreshProgramItems } from './loadProgramItems';
Chart.register(ArcElement, Tooltip, Legend);

const PROGRAM_TYPES = [
    'Aerobic Training',
    'Lactate Threshold Training',
    'VO2max Training',
    'Anaerobic Training',
    'Race Warm Up',
    'Default Training'
];

const SET_TYPES = ['warm_up', 'pre_set', 'drill_set', 'main_set', 'cool_down'];
const SET_TYPES_DISPLAY = {
    warm_up: 'Warm Up',
    pre_set: 'Pre Set',
    drill_set: 'Drill Set',
    main_set: 'Main Set',
    cool_down: 'Cool Down'
};

const SWIMMING_STYLES = [
    'freestyle',
    'backStroke',
    'breastStroke',
    'butterfly',
    'individualMedley'
];

const SWIMMING_STYLES_DISPLAY = {
    freestyle: 'Free Style',
    backStroke: 'Back Stroke',
    breastStroke: 'Breast Stroke',
    butterfly: 'Butterfly',
    individualMedley: 'Individual Medley'
};

const ZONES = ['rec', 'end1', 'end2', 'end3', 'sp1', 'sp2', 'sp3'];

const SubItemCell = ({ children, isSubItem }) => {
    if (isSubItem) {
        return <div style={{ color: '#666', fontWeight: 'normal' }}>{children}</div>;
    }
    return <div style={{ color: '#333', fontWeight: 'bold' }}>{children}</div>;
};

const ContextMenu = ({
    isOpen,
    onClose,
    onAddSubItem,
    onEdit,
    onDelete,
    canAddSubItem,
    position
}) => {
    if (!isOpen) return null;

    return (
        <>
            <div className={styles.contextMenuOverlay} onClick={onClose} />
            <div
                className={styles.contextMenu}
                style={{
                    left: position.x,
                    top: position.y
                }}
            >
                {canAddSubItem && (
                    <button className={styles.contextMenuItem} onClick={onAddSubItem}>
                        <MdAdd size={16} />
                        Add Sub-Item
                    </button>
                )}
                <button className={styles.contextMenuItem} onClick={onEdit}>
                    <MdEdit size={16} />
                    Edit
                </button>
                <button className={styles.contextMenuItem} onClick={onDelete}>
                    <MdDelete size={16} />
                    Delete
                </button>
            </div>
        </>
    );
};

const SubItemForm = ({ onSubmit, onCancel, register, handleSubmit, errors }) => {
    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.modalContent}>
            <div className={styles.formGroup}>
                <label>Swimming Style</label>
                <select
                    {...register('swimmingStyle', {
                        required: 'Swimming style is required'
                    })}
                >
                    {SWIMMING_STYLES.map(style => (
                        <option key={style} value={style}>
                            {SWIMMING_STYLES_DISPLAY[style]}
                        </option>
                    ))}
                </select>
                {errors.swimmingStyle && (
                    <span className={styles.error}>{errors.swimmingStyle.message}</span>
                )}
            </div>
            <div className={styles.formGroup}>
                <label>Zone</label>
                <select {...register('zones', { required: 'Zone is required' })}>
                    {ZONES.map(zone => (
                        <option key={zone} value={zone}>
                            {zone.toUpperCase()}
                        </option>
                    ))}
                </select>
                {errors.zones && <span className={styles.error}>{errors.zones.message}</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className={styles.formGroup}>
                    <label>Repetitions</label>
                    <input
                        type='number'
                        min='1'
                        {...register('reps', {
                            required: 'Repetitions is required',
                            min: { value: 1, message: 'Repetitions must be at least 1' }
                        })}
                    />
                    {errors.reps && <span className={styles.error}>{errors.reps.message}</span>}
                </div>
                <div className={styles.formGroup}>
                    <label>Distance (meters)</label>
                    <input
                        type='number'
                        min='0'
                        step='0.5'
                        {...register('distance', {
                            required: 'Distance is required',
                            min: { value: 0, message: 'Distance cannot be negative' }
                        })}
                    />
                    {errors.distance && (
                        <span className={styles.error}>{errors.distance.message}</span>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className={styles.formGroup}>
                    <label>Rest Between Reps (sec)</label>
                    <input
                        type='number'
                        min='0'
                        {...register('repsRest', {
                            min: { value: 0, message: 'Rest cannot be negative' }
                        })}
                    />
                    {errors.repsRest && (
                        <span className={styles.error}>{errors.repsRest.message}</span>
                    )}
                </div>
                <div className={styles.formGroup}>
                    <label>Rest Between Sets (sec)</label>
                    <input
                        type='number'
                        min='0'
                        {...register('setRest', {
                            min: { value: 0, message: 'Rest cannot be negative' }
                        })}
                    />
                    {errors.setRest && (
                        <span className={styles.error}>{errors.setRest.message}</span>
                    )}
                </div>
            </div>
            <div className={styles.formGroup}>
                <label>Rest After Set (sec)</label>
                <input
                    type='number'
                    min='0'
                    {...register('afterSetRest', {
                        min: { value: 0, message: 'Rest cannot be negative' }
                    })}
                />
                {errors.afterSetRest && (
                    <span className={styles.error}>{errors.afterSetRest.message}</span>
                )}
            </div>
            <div className={styles.formGroup}>
                <label>Comments</label>
                <input type='text' {...register('comment')} />
            </div>
            <div className={styles.modalActions}>
                <button type='submit' className={styles.submitBtn}>
                    Create Sub-Item
                </button>
                <button type='button' className={styles.cancelBtn} onClick={onCancel}>
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default function ProgramCreator() {
    const { user } = useUser();
    const { coachId } = useParams();
    const visitedUser = useVisitingUser(coachId);
    const currentUser = coachId ? visitedUser : user;

    const [programs, setPrograms] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateItemModal, setShowCreateItemModal] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [programItems, setProgramItems] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const focusedProgramIdRef = useRef(null);
    const [editing, setEditing] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [activeDonut, setActiveDonut] = useState(0);
    const [programTypeFilter, setProgramTypeFilter] = useState('All');
    const [showCreateSubItemModal, setShowCreateSubItemModal] = useState(false);
    const [parentItemForSubItem, setParentItemForSubItem] = useState(null);
    const [contextMenu, setContextMenu] = useState({
        isOpen: false,
        item: null,
        position: { x: 0, y: 0 }
    });

    const {
        register: registerProgram,
        handleSubmit: handleProgramSubmit,
        reset: resetProgram,
        formState: { errors: programErrors }
    } = useForm({
        defaultValues: {
            name: '',
            type: PROGRAM_TYPES[0]
        }
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            setType: SET_TYPES[0],
            swimmingStyle: SWIMMING_STYLES[0],
            zones: ZONES[0],
            set: 1,
            reps: 1,
            distance: 0,
            repsRest: 0,
            setRest: 0,
            afterSetRest: 0,
            rest: 0,
            comment: ''
        }
    });

    useEffect(() => {
        const loadPrograms = async () => {
            try {
                const { data } = await API.getMyPrograms(currentUser._id);
                setPrograms(data);
            } catch (error) {
                toast.error('Failed to load programs');
            }
        };

        if (currentUser) {
            loadPrograms();
        }
    }, [currentUser]);

    const handleCreateProgram = async data => {
        try {
            const { data: responseData } = await API.createProgram({
                ...data,
                coach: currentUser._id
            });
            setPrograms(prev => [...prev, responseData]);
            setShowCreateModal(false);
            resetProgram();
            toast.success('Program created successfully');
        } catch (error) {
            toast.error('Failed to create program');
        }
    };

    const handleDeleteProgram = async () => {
        try {
            await API.deleteProgram(selectedProgram._id);
            setPrograms(prev => prev.filter(p => p._id !== selectedProgram._id));
            setShowDeleteModal(false);
            setSelectedProgram(null);
            toast.success('Program deleted successfully');
        } catch (error) {
            toast.error('Failed to delete program');
        }
    };

    const handleEditProgram = async data => {
        try {
            setEditing(true);
            const { data: responseData } = await API.updateProgram(selectedProgram._id, data);
            setPrograms(prev => prev.map(p => (p._id === selectedProgram._id ? responseData : p)));
            setShowEditModal(false);
            setSelectedProgram(null);
            resetProgram();
            toast.success('Program updated successfully');
        } catch (error) {
            toast.error('Failed to update program');
        } finally {
            setEditing(false);
        }
    };

    const refreshItemsFor = programId =>
        refreshProgramItems(programId, {
            fetchItems: API.getProgramItems,
            setItems: setProgramItems,
            isStale: () => focusedProgramIdRef.current !== programId
        });

    const handleProgramFocus = program => {
        focusedProgramIdRef.current = program._id;
        return focusProgram(program, {
            fetchItems: API.getProgramItems,
            setSelected: setSelectedProgram,
            setItems: setProgramItems,
            setLoading: setItemsLoading,
            isStale: () => focusedProgramIdRef.current !== program._id,
            onError: () => toast.error('Failed to load program items')
        });
    };

    const handleCreateProgramItem = async data => {
        if (!selectedProgram) return;
        try {
            await API.createProgramItem(selectedProgram._id, {
                ...data
            });

            await refreshItemsFor(selectedProgram._id);
            setShowCreateItemModal(false);
            reset();
            toast.success('Program item created successfully');
        } catch (error) {
            toast.error('Failed to create program item');
        }
    };

    const handleCreateSubItem = async data => {
        if (!selectedProgram || !parentItemForSubItem) return;
        try {
            const { set, ...subItemData } = data;

            await API.createSubItem(selectedProgram._id, parentItemForSubItem._id, {
                ...subItemData,
                setType: parentItemForSubItem.setType
            });

            await refreshItemsFor(selectedProgram._id);
            setShowCreateSubItemModal(false);
            setParentItemForSubItem(null);
            reset();
            toast.success('Sub-item created successfully');
        } catch (error) {
            toast.error('Failed to create sub-item');
        }
    };

    const flattenProgramItems = items => {
        const flattened = [];
        items.forEach(item => {
            // Add the main item
            flattened.push(item);
            // Add sub-items if they exist
            if (item.subItems && item.subItems.length > 0) {
                item.subItems.forEach(subItem => {
                    flattened.push({
                        ...subItem,
                        _isSubItem: true,
                        _parentId: item._id
                    });
                });
            }
        });
        return flattened;
    };

    const flattenedProgramItems = flattenProgramItems(programItems);

    const handleEditProgramItem = async data => {
        if (!selectedProgram || !editingItem) return;
        try {
            setEditing(true);
            await API.updateProgramItem(selectedProgram._id, editingItem._id, {
                setType: data.setType,
                swimmingStyle: data.swimmingStyle,
                zones: data.zones,
                set: data.set,
                reps: data.reps,
                distance: data.distance,
                repsRest: data.repsRest,
                setRest: data.setRest,
                afterSetRest: data.afterSetRest,
                rest: data.rest,
                comment: data.comment
            });

            await refreshItemsFor(selectedProgram._id);

            setShowCreateItemModal(false);
            setEditingItem(null);
            reset();
            toast.success('Program item updated successfully');
        } catch (error) {
            toast.error('Failed to update program item');
        } finally {
            setEditing(false);
        }
    };

    const handleDeleteProgramItem = async () => {
        if (!selectedProgram || !itemToDelete) return;
        try {
            await API.deleteProgramItem(selectedProgram._id, itemToDelete._id);

            await refreshItemsFor(selectedProgram._id);
            setShowDeleteItemModal(false);
            setItemToDelete(null);

            toast.success('Program item deleted successfully');
        } catch (error) {
            toast.error('Failed to delete program item');
        }
    };

    const handleMoveItem = async (itemId, direction) => {
        if (!selectedProgram) return;
        try {
            const topLevelItems = programItems.filter(item => !item.parent);
            const currentIndex = topLevelItems.findIndex(item => item._id === itemId);

            if (currentIndex === -1) {
                toast.error('Can only reorder top-level items');
                return;
            }

            if (
                (direction === 'up' && currentIndex === 0) ||
                (direction === 'down' && currentIndex === topLevelItems.length - 1)
            ) {
                return;
            }

            const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

            const updatedTopLevelItems = [...topLevelItems];

            [updatedTopLevelItems[currentIndex], updatedTopLevelItems[newIndex]] = [
                updatedTopLevelItems[newIndex],
                updatedTopLevelItems[currentIndex]
            ];

            const newOrder = updatedTopLevelItems.map(item => item._id);
            const { data } = await API.reorderProgramItems(selectedProgram._id, newOrder);

            if (focusedProgramIdRef.current === selectedProgram._id) {
                setProgramItems(data.sort((a, b) => a.order - b.order));
            }
            toast.success('Program items reordered successfully');
        } catch (error) {
            await refreshItemsFor(selectedProgram._id);
            toast.error('Failed to reorder program items');
        }
    };

    // Color mappings for set types (matching Training Information donut chart)
    const SET_TYPE_COLORS = {
        warm_up: '#F59E42',      // Orange
        pre_set: '#F7C873',      // Light Yellow
        drill_set: '#A3E635',    // Lime Green
        main_set: '#38BDF8',     // Sky Blue
        cool_down: '#6366F1'     // Indigo
    };

    // Color mappings for zones (matching Training Information donut chart)
    const ZONE_COLORS = {
        rec: '#F87171',          // Red
        end1: '#FBBF24',         // Amber
        end2: '#34D399',         // Emerald
        end3: '#60A5FA',         // Sky Blue
        sp1: '#A78BFA',          // Violet
        sp2: '#F472B6',          // Pink
        sp3: '#D1D5DB'           // Gray
    };

    const programItemColumns = [
        {
            Header: 'Set Type',
            accessor: 'setType',
            Cell: ({ value, row }) => {
                if (row.original._isSubItem) return null;
                const displayValue = SET_TYPES_DISPLAY[value] || '-';
                const color = SET_TYPE_COLORS[value] || '#64748b';
                return (
                    <span style={{
                        backgroundColor: color,
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                    }}>
                        {displayValue}
                    </span>
                );
            }
        },
        {
            id: 'set',
            Header: () => <div style={{ textAlign: 'center' }}>Set</div>,
            accessor: 'set',
            Cell: ({ value, row }) => (
                row.original._isSubItem ? null : (
                    <div style={{ textAlign: 'center' }}>{value || '-'}</div>
                )
            )
        },
        {
            id: 'reps',
            Header: () => <div style={{ textAlign: 'center' }}>Reps</div>,
            accessor: 'reps',
            Cell: ({ value, row }) => (
                <div style={{ textAlign: 'center' }}>
                    <SubItemCell isSubItem={row.original._isSubItem}>{value || '-'}</SubItemCell>
                </div>
            )
        },
        {
            id: 'distance',
            Header: () => <div style={{ textAlign: 'center' }}>Distance (m.)</div>,
            accessor: 'distance',
            Cell: ({ value, row }) => (
                <div style={{ textAlign: 'center' }}>
                    <SubItemCell isSubItem={row.original._isSubItem}>
                        {value ? `${value} m` : '-'}
                    </SubItemCell>
                </div>
            )
        },
        {
            Header: 'Swimming Style',
            accessor: 'swimmingStyle',
            Cell: ({ value, row }) => (
                <SubItemCell isSubItem={row.original._isSubItem}>
                    {SWIMMING_STYLES_DISPLAY[value] || '-'}
                </SubItemCell>
            )
        },
        {
            id: 'zone',
            Header: () => <div style={{ textAlign: 'center' }}>Zone</div>,
            accessor: 'zones',
            Cell: ({ value, row }) => {
                if (!value) {
                    return (
                        <div style={{ textAlign: 'center' }}>
                            <SubItemCell isSubItem={row.original._isSubItem}>-</SubItemCell>
                        </div>
                    );
                }
                const color = ZONE_COLORS[value] || '#64748b';
                return (
                    <div style={{ textAlign: 'center' }}>
                        <SubItemCell isSubItem={row.original._isSubItem}>
                            <span style={{
                                backgroundColor: color,
                                color: value === 'sp3' ? '#1f2937' : 'white',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                display: 'inline-block',
                                textTransform: 'uppercase'
                            }}>
                                {value.toUpperCase()}
                            </span>
                        </SubItemCell>
                    </div>
                );
            }
        },
        {
            Header: 'Comments',
            accessor: 'comment',
            width: 150,
            maxWidth: 150,
            minWidth: 150,
            Cell: ({ value, row }) => (
                <div
                    style={{
                        maxWidth: '150px',
                        width: '150px',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        lineHeight: '1.4',
                        padding: row.original._isSubItem ? '8px 8px 8px 32px' : '8px'
                    }}
                >
                    {value || '-'}
                </div>
            )
        },
        {
            id: 'repsRest',
            Header: () => <div style={{ textAlign: 'center' }}>Reps Rest (sec)</div>,
            accessor: 'repsRest',
            Cell: ({ value, row }) => (
                <div style={{ textAlign: 'center' }}>
                    <SubItemCell isSubItem={row.original._isSubItem}>
                        {value ? `${value} s` : '-'}
                    </SubItemCell>
                </div>
            )
        },
        {
            id: 'setRest',
            Header: () => <div style={{ textAlign: 'center' }}>Set Rest (sec)</div>,
            accessor: 'setRest',
            Cell: ({ value, row }) => (
                <div style={{ textAlign: 'center' }}>
                    <SubItemCell isSubItem={row.original._isSubItem}>
                        {value ? `${value} s` : '-'}
                    </SubItemCell>
                </div>
            )
        },
        {
            id: 'afterSetRest',
            Header: () => <div style={{ textAlign: 'center' }}>After Set Rest (sec)</div>,
            accessor: 'afterSetRest',
            Cell: ({ value, row }) => (
                <div style={{ textAlign: 'center' }}>
                    <SubItemCell isSubItem={row.original._isSubItem}>
                        {value ? `${value} s` : '-'}
                    </SubItemCell>
                </div>
            )
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            Cell: ({ row }) => (
                <div className={styles.actionsCell}>
                    {/* Only show reorder buttons for top-level items */}
                    {!row.original._isSubItem && (
                        <>
                            <button
                                className={styles.actionBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveItem(row.original._id, 'up');
                                }}
                                disabled={row.index === 0}
                                title='Move up'
                            >
                                <MdArrowUpward size={20} />
                            </button>
                            <button
                                className={styles.actionBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveItem(row.original._id, 'down');
                                }}
                                disabled={row.index === flattenedProgramItems.length - 1}
                                title='Move down'
                            >
                                <MdArrowDownward size={20} />
                            </button>
                        </>
                    )}
                    <button
                        className={styles.actionBtn}
                        onClick={event => handleContextMenu(event, row.original)}
                        title='More options'
                    >
                        <MdMoreVert size={20} />
                    </button>
                </div>
            )
        }
    ];

    // Calculate sums for summary island
    const totalDistance = Utils.calculateTotalProgramDistance(programItems);
    const warmUpDistance = Math.round(totalDistance * 0.15);
    const coolDownDistance = Math.round(totalDistance * 0.05);

    const swimmingStyleCounts = Utils.calculateSwimmingStyleStats(programItems, SWIMMING_STYLES);

    const donutData = {
        labels: SWIMMING_STYLES.map(style => SWIMMING_STYLES_DISPLAY[style]),
        datasets: [
            {
                data: swimmingStyleCounts,
                backgroundColor: ['#1E40AF', '#60A5FA', '#1E293B', '#A5B4FC', '#E5E7EB'],
                borderWidth: 1
            }
        ]
    };

    const donutOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 16
                }
            }
        },
        cutout: '50%'
    };

    const setTypeCounts = Utils.calculateSetTypeStats(programItems, SET_TYPES);

    const setTypeDonutData = {
        labels: SET_TYPES.map(type => SET_TYPES_DISPLAY[type]),
        datasets: [
            {
                data: setTypeCounts,
                backgroundColor: ['#F59E42', '#F7C873', '#A3E635', '#38BDF8', '#6366F1'],
                borderWidth: 1
            }
        ]
    };

    const setTypeDonutOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 16
                }
            }
        },
        cutout: '50%'
    };

    const ZONE_LABELS = ZONES.map(z => z.toUpperCase());
    const zoneCounts = Utils.calculateZoneStats(programItems, ZONES);
    const zoneDonutData = {
        labels: ZONE_LABELS,
        datasets: [
            {
                data: zoneCounts,
                backgroundColor: [
                    '#F87171',
                    '#FBBF24',
                    '#34D399',
                    '#60A5FA',
                    '#A78BFA',
                    '#F472B6',
                    '#D1D5DB'
                ],
                borderWidth: 1
            }
        ]
    };
    const zoneDonutOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 16 }
            }
        },
        cutout: '50%'
    };

    const donutConfigs = [
        {
            data: donutData,
            options: donutOptions,
            label: 'Swimming Styles'
        },
        {
            data: setTypeDonutData,
            options: setTypeDonutOptions,
            label: 'Set Types'
        },
        {
            data: zoneDonutData,
            options: zoneDonutOptions,
            label: 'Swimming Zones'
        }
    ];

    const handleDonutCarousel = direction => {
        setActiveDonut(prev => (direction === 'right' ? (prev + 1) % 3 : (prev + 2) % 3));
    };

    const handleContextMenu = (event, item) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            isOpen: true,
            item,
            position: { x: event.clientX, y: event.clientY }
        });
    };

    const closeContextMenu = () => {
        setContextMenu({ isOpen: false, item: null, position: { x: 0, y: 0 } });
    };

    const handleContextMenuAction = action => {
        const item = contextMenu.item;
        closeContextMenu();

        switch (action) {
            case 'addSubItem':
                setParentItemForSubItem(item);
                setShowCreateSubItemModal(true);
                break;
            case 'edit':
                setEditingItem(item);
                reset({
                    setType: item.setType,
                    swimmingStyle: item.swimmingStyle,
                    zones: item.zones,
                    set: item.set,
                    reps: item.reps,
                    distance: item.distance,
                    repsRest: item.repsRest,
                    setRest: item.setRest,
                    afterSetRest: item.afterSetRest,
                    rest: item.rest,
                    comment: item.comment
                });
                setShowCreateItemModal(true);
                break;
            case 'delete':
                setItemToDelete(item);
                setShowDeleteItemModal(true);
                break;
            default:
                break;
        }
    };

    const filteredPrograms =
        programTypeFilter === 'All' ? programs : programs.filter(p => p.type === programTypeFilter);

    return (
        <div className={styles.programCreator}>
            <div className={styles.programsSection + ' island'}>
                <div className={styles.programsHeader}>
                    <h2 className={styles.sectionTitle}>Programs</h2>

                    <button
                        className={styles.addBtn}
                        onClick={() => {
                            setShowCreateModal(true);
                        }}
                        title='Create new program'
                    >
                        <MdAdd size={24} />
                    </button>
                </div>
                <select
                    className={styles.programTypeDropdown}
                    value={programTypeFilter}
                    onChange={e => setProgramTypeFilter(e.target.value)}
                    style={{ marginLeft: 16, minWidth: 160 }}
                >
                    <option value='All'>All</option>
                    {PROGRAM_TYPES.map(type => (
                        <option key={type} value={type}>
                            {type}
                        </option>
                    ))}
                </select>
                <br />
                {programs.length === 0 && (
                    <div className={styles.emptyState}>No programs found.</div>
                )}
                <div className={styles.programList}>
                    {filteredPrograms.map(program => {
                        const isSelected = selectedProgram?._id === program._id;
                        return (
                            <div
                                key={program._id}
                                className={`${styles.programCard} ${isSelected ? styles.selectedProgram : ''}`}
                            >
                                <button
                                    type='button'
                                    className={styles.programSelect}
                                    aria-pressed={isSelected}
                                    onClick={() => handleProgramFocus(program)}
                                >
                                    <span className={styles.programTitle}>{program.name}</span>
                                    <span className={styles.programType}>{program.type}</span>
                                    <span className={styles.programCreated}>
                                        Created: {new Date(program.createdAt).toLocaleDateString()}
                                    </span>
                                </button>
                                <div className={styles.programActions}>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={() => {
                                            setSelectedProgram(program);
                                            resetProgram({ name: program.name, type: program.type });
                                            setShowEditModal(true);
                                        }}
                                        title='Edit program'
                                    >
                                        <MdEdit size={20} />
                                    </button>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={() => {
                                            setSelectedProgram(program);
                                            setShowDeleteModal(true);
                                        }}
                                        title='Delete program'
                                    >
                                        <MdDelete size={20} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className={styles.programItemsWrapper}>
                {selectedProgram && (
                    <div className={styles.programItemsSection}>
                        <div className={styles.programItemsHeader}>
                            <h2 className={styles.sectionTitle}>
                                Program analysis for {selectedProgram.name}
                            </h2>
                            <button
                                className={styles.addBtn}
                                onClick={() => {
                                    setShowCreateItemModal(true);
                                }}
                                title='Add new program item'
                            >
                                <MdAdd size={24} />
                            </button>
                        </div>

                        <div className={styles.tableContainer}>
                            <Table
                                columns={programItemColumns}
                                data={flattenedProgramItems}
                                resetPageKey={selectedProgram?._id}
                                isLoading={itemsLoading}
                            />
                        </div>
                    </div>
                )}
                {selectedProgram && (
                    <div className={styles.summaryIsland + ' island'}>
                        <h2 className={styles.sectionTitle}>Training Information</h2>
                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryFields}>
                                <div>
                                    <label>Total Training Distance (m):</label>
                                    <input
                                        type='number'
                                        value={totalDistance}
                                        disabled
                                        className='form-control'
                                    />
                                </div>
                                <div>
                                    <label>Ideal Warm Up Distance (m):</label>
                                    <input
                                        type='number'
                                        value={warmUpDistance}
                                        disabled
                                        className='form-control'
                                    />
                                </div>
                                <div>
                                    <label>Ideal Cool Down Distance (m):</label>
                                    <input
                                        type='number'
                                        value={coolDownDistance}
                                        disabled
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.donutChartWrapper}>
                                <div className={styles.donutChartNav}>
                                    <button
                                        className={styles.donutNavBtn}
                                        onClick={() => handleDonutCarousel('left')}
                                        title='Previous chart'
                                    >
                                        <MdChevronLeft size={24} />
                                    </button>
                                    <span className={styles.donutChartTitle}>
                                        {donutConfigs[activeDonut].label}
                                    </span>
                                    <button
                                        className={styles.donutNavBtn}
                                        onClick={() => handleDonutCarousel('right')}
                                        title='Next chart'
                                    >
                                        <MdChevronRight size={24} />
                                    </button>
                                </div>
                                {itemsLoading ? (
                                    <div className={styles.emptyState}>Loading program items...</div>
                                ) : (
                                    <Doughnut
                                        data={donutConfigs[activeDonut].data}
                                        options={donutConfigs[activeDonut].options}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showCreateModal && (
                <Modal
                    title='Create New Program'
                    closeCB={() => {
                        setShowCreateModal(false);
                        resetProgram();
                    }}
                >
                    <form
                        onSubmit={handleProgramSubmit(handleCreateProgram)}
                        className={styles.modalContent}
                    >
                        <div className={styles.formGroup}>
                            <label>Program Name</label>
                            <input
                                type='text'
                                {...registerProgram('name', {
                                    required: 'Program name is required'
                                })}
                                placeholder='Enter program name'
                            />
                            {programErrors.name && (
                                <span className={styles.error}>{programErrors.name.message}</span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label>Program Type</label>
                            <select
                                {...registerProgram('type', {
                                    required: 'Program type is required'
                                })}
                            >
                                {PROGRAM_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            {programErrors.type && (
                                <span className={styles.error}>{programErrors.type.message}</span>
                            )}
                        </div>
                        <div className={styles.modalActions}>
                            <button type='submit' className={styles.submitBtn}>
                                Create Program
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showEditModal && (
                <Modal
                    title='Edit Program'
                    closeCB={() => {
                        setShowEditModal(false);
                        setSelectedProgram(null);
                        resetProgram();
                    }}
                >
                    <form
                        onSubmit={handleProgramSubmit(handleEditProgram)}
                        className={styles.modalContent}
                    >
                        <div className={styles.formGroup}>
                            <label>Program Name</label>
                            <input
                                type='text'
                                {...registerProgram('name', {
                                    required: 'Program name is required'
                                })}
                                placeholder='Enter program name'
                            />
                            {programErrors.name && (
                                <span className={styles.error}>{programErrors.name.message}</span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label>Program Type</label>
                            <select
                                {...registerProgram('type', {
                                    required: 'Program type is required'
                                })}
                            >
                                {PROGRAM_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            {programErrors.type && (
                                <span className={styles.error}>{programErrors.type.message}</span>
                            )}
                        </div>
                        <div className={styles.modalActions}>
                            <button type='submit' className={styles.submitBtn} disabled={editing}>
                                {editing ? 'Updating...' : 'Update Program'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showDeleteModal && (
                <Modal
                    title='Delete Program'
                    closeCB={() => {
                        setShowDeleteModal(false);
                        setSelectedProgram(null);
                    }}
                >
                    <div className={styles.modalContent}>
                        <p>Are you sure you want to delete this program?</p>
                        <div className={styles.modalActions}>
                            <button className={styles.deleteBtn} onClick={handleDeleteProgram}>
                                Delete Program
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showCreateItemModal && (
                <Modal
                    title={editingItem ? 'Edit Program Item' : 'Create New Program Item'}
                    closeCB={() => {
                        setShowCreateItemModal(false);
                        setEditingItem(null);
                        reset();
                    }}
                >
                    <form
                        onSubmit={handleSubmit(
                            editingItem ? handleEditProgramItem : handleCreateProgramItem
                        )}
                        className={styles.modalContent}
                    >
                        <div className={styles.formGroup}>
                            <label>Set Type</label>
                            <select {...register('setType', { required: 'Set type is required' })}>
                                {SET_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {SET_TYPES_DISPLAY[type]}
                                    </option>
                                ))}
                            </select>
                            {errors.setType && (
                                <span className={styles.error}>{errors.setType.message}</span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label>Swimming Style</label>
                            <select
                                {...register('swimmingStyle', {
                                    required: 'Swimming style is required'
                                })}
                            >
                                {SWIMMING_STYLES.map(style => (
                                    <option key={style} value={style}>
                                        {SWIMMING_STYLES_DISPLAY[style]}
                                    </option>
                                ))}
                            </select>
                            {errors.swimmingStyle && (
                                <span className={styles.error}>{errors.swimmingStyle.message}</span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label>Zone</label>
                            <select {...register('zones', { required: 'Zone is required' })}>
                                {ZONES.map(zone => (
                                    <option key={zone} value={zone}>
                                        {zone.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                            {errors.zones && (
                                <span className={styles.error}>{errors.zones.message}</span>
                            )}
                        </div>

                        <div
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}
                        >
                            <div className={styles.formGroup}>
                                <label>Sets</label>
                                <input
                                    type='number'
                                    min='1'
                                    {...register('set', {
                                        required: 'Sets is required',
                                        min: { value: 1, message: 'Sets must be at least 1' }
                                    })}
                                />
                                {errors.set && (
                                    <span className={styles.error}>{errors.set.message}</span>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label>Repetitions</label>
                                <input
                                    type='number'
                                    min='1'
                                    {...register('reps', {
                                        required: 'Repetitions is required',
                                        min: { value: 1, message: 'Repetitions must be at least 1' }
                                    })}
                                />
                                {errors.reps && (
                                    <span className={styles.error}>{errors.reps.message}</span>
                                )}
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Distance (meters)</label>
                            <input
                                type='number'
                                min='0'
                                step='0.5'
                                {...register('distance', {
                                    required: 'Distance is required',
                                    min: { value: 0, message: 'Distance cannot be negative' }
                                })}
                            />
                            {errors.distance && (
                                <span className={styles.error}>{errors.distance.message}</span>
                            )}
                        </div>
                        <div
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}
                        >
                            <div className={styles.formGroup}>
                                <label>Rest Between Reps (sec)</label>
                                <input
                                    type='number'
                                    min='0'
                                    {...register('repsRest', {
                                        min: { value: 0, message: 'Rest cannot be negative' }
                                    })}
                                />
                                {errors.repsRest && (
                                    <span className={styles.error}>{errors.repsRest.message}</span>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label>Rest Between Sets (sec)</label>
                                <input
                                    type='number'
                                    min='0'
                                    {...register('setRest', {
                                        min: { value: 0, message: 'Rest cannot be negative' }
                                    })}
                                />
                                {errors.setRest && (
                                    <span className={styles.error}>{errors.setRest.message}</span>
                                )}
                            </div>
                        </div>
                        <div className={styles.formGroup}>
                            <label>Rest After Set (sec)</label>
                            <input
                                type='number'
                                min='0'
                                {...register('afterSetRest', {
                                    min: { value: 0, message: 'Rest cannot be negative' }
                                })}
                            />
                            {errors.afterSetRest && (
                                <span className={styles.error}>
                                    {errors.afterSetRest.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label>Comments</label>
                            <input type='text' {...register('comment')} />
                        </div>
                        <div className={styles.modalActions}>
                            <button type='submit' className={styles.submitBtn} disabled={editing}>
                                {editingItem
                                    ? editing
                                        ? 'Updating...'
                                        : 'Update Item'
                                    : 'Create Item'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showDeleteItemModal && (
                <Modal
                    title='Delete Program Item'
                    closeCB={() => {
                        setShowDeleteItemModal(false);
                        setItemToDelete(null);
                    }}
                >
                    <div className={styles.modalContent}>
                        <p>Are you sure you want to delete this program item?</p>
                        <div className={styles.modalActions}>
                            <button className={styles.deleteBtn} onClick={handleDeleteProgramItem}>
                                Delete Item
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showCreateSubItemModal && (
                <Modal
                    title={`Create Sub-Item for ${
                        parentItemForSubItem?.setType
                            ? SET_TYPES_DISPLAY[parentItemForSubItem.setType]
                            : 'Program Item'
                    }`}
                    closeCB={() => {
                        setShowCreateSubItemModal(false);
                        setParentItemForSubItem(null);
                        reset();
                    }}
                >
                    <SubItemForm
                        onSubmit={handleSubmit(handleCreateSubItem)}
                        onCancel={() => {
                            setShowCreateSubItemModal(false);
                            setParentItemForSubItem(null);
                            reset();
                        }}
                        register={register}
                        handleSubmit={handleSubmit}
                        errors={errors}
                    />
                </Modal>
            )}

            <ContextMenu
                isOpen={contextMenu.isOpen}
                onClose={closeContextMenu}
                onAddSubItem={() => handleContextMenuAction('addSubItem')}
                onEdit={() => handleContextMenuAction('edit')}
                onDelete={() => handleContextMenuAction('delete')}
                canAddSubItem={
                    contextMenu.item && !contextMenu.item.parent && !contextMenu.item._isSubItem
                }
                position={contextMenu.position}
            />
        </div>
    );
}
